import { useEffect, useState, useRef, useCallback } from "react";

import { useAppDispatch, useAppSelector } from "../../../../hooks/redux";
import {
  setSort,
  setSearchParam,
  setPageSize,
} from "../../../../redux/reducers/filter";
import { useSearchProvidersPaginatedQuery } from "../../../../redux/reduxQuery/medicalProviderApi";

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface SortState {
  id: string;
  desc: boolean;
}

export interface TableConfig {
  loading?: boolean;
  data?: unknown[];
  asyncConfig?: {
    pageSize: number;
    pageIndex: number;
    pageCount: number;
    onPaginationChange: (
      pagination: PaginationState | ((prev: PaginationState) => PaginationState)
    ) => void;
    onSortingChange: (
      sorting: SortState[] | ((prev: SortState[]) => SortState[])
    ) => void;
    onSearch: (search: string) => void;
    sort: SortState[];
    facets: { search: string };
  };
}

export const useGetData = (initialQs = "", pageSize = 50) => {
  const dispatch = useAppDispatch();
  const [dataToFetch, setDataToFetch] = useState(initialQs);

  const storedSort = useAppSelector(
    (state) => state.filter?.medicalControl?.sort
  );
  const storedSearch = useAppSelector(
    (state) => state.filter?.medicalControl?.searchParam
  );
  const storedPageSize = useAppSelector(
    (state) => state.filter?.medicalControl?.pageSize
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: Number(storedPageSize ?? pageSize),
  });

  const [sorting, setSorting] = useState<SortState[]>(() => {
    if (Array.isArray(storedSort) && storedSort.length > 0) {
      return storedSort as SortState[];
    }
    return [{ id: "createdAt", desc: true }];
  });

  const [search, setSearch] = useState<string>(
    typeof storedSearch === "string" ? storedSearch : ""
  );

  // RTK Query hook - handles caching and deduplication automatically
  const { data, isLoading, isFetching, refetch } =
    useSearchProvidersPaginatedQuery({
      filter: dataToFetch,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sort: sorting[0] || null,
      search: search,
    });

  // Sync sort to Redux for persistence (only when changed)
  const prevSortRef = useRef<string | null>(null);
  useEffect(() => {
    const sortStr = JSON.stringify(sorting);
    if (prevSortRef.current !== sortStr) {
      prevSortRef.current = sortStr;
      dispatch(
        setSort({
          medicalControl: {
            sort: sorting && sorting.length > 0 ? sorting : null,
          },
        })
      );
    }
  }, [sorting, dispatch]);

  // Sync search to Redux for persistence (only when changed)
  const prevSearchRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevSearchRef.current !== search) {
      prevSearchRef.current = search;
      dispatch(
        setSearchParam({
          medicalControl: { search: search },
        })
      );
    }
  }, [search, dispatch]);

  // Sync page size to Redux for persistence (only when changed)
  const prevPageSizeRef = useRef<number | null>(null);
  useEffect(() => {
    if (prevPageSizeRef.current !== pagination.pageSize) {
      prevPageSizeRef.current = pagination.pageSize;
      dispatch(
        setPageSize({
          medicalControl: { pageSize: pagination.pageSize },
        })
      );
    }
  }, [pagination.pageSize, dispatch]);

  // Stable callback handlers
  const onPaginationChange = useCallback(
    (
      newPagination:
        | PaginationState
        | ((prev: PaginationState) => PaginationState)
    ) => {
      setPagination(newPagination);
    },
    []
  );

  const onSortingChange = useCallback(
    (newSorting: SortState[] | ((prev: SortState[]) => SortState[])) => {
      setSorting(newSorting);
    },
    []
  );

  const handleSearch = useCallback((newSearch: string) => {
    setSearch(newSearch);
    // Reset to first page when searching
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  return {
    config: {
      data: data?.providers ?? [],
      loading: isLoading || isFetching,
      asyncConfig: {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
        pageCount: data ? Math.ceil(data.total / pagination.pageSize) : 0,
        onPaginationChange,
        onSortingChange,
        onSearch: handleSearch,
        sort: sorting,
        facets: { search },
      },
    } as TableConfig,
    refetch,
    dataToFetch,
    setDataToFetch,
  };
};
